import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, money, d } from "@/api";
import { Badge, MODE_LABELS, MarginTag, DIRECTION_LABELS } from "@/components/Badge";
import { useAuth } from "@/context/AuthContext";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];

export default function RequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [offer, setOffer] = useState({ supplier_id: "", amount: "", currency: "USD", transit_days: 0, notes: "" });
  const [quote, setQuote] = useState({ cost_amount: "", cost_currency: "USD", sale_amount: "", sale_currency: "USD", valid_until: "", fx_locked: false, notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/requests/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => {
    load();
    api.get("/parties", { params: { type: "tedarikci" } }).then((r) => setSuppliers(r.data)).catch(() => {});
  }, [load]);

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-klein" /></div>;
  const req = data.request;
  const selected = data.supplier_offers.find((o) => o.selected);

  const addOffer = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/requests/${id}/offers`, { ...offer, amount: Number(offer.amount), transit_days: Number(offer.transit_days) });
      toast.success("Tedarikçi teklifi eklendi");
      setOffer({ supplier_id: "", amount: "", currency: "USD", transit_days: 0, notes: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const select = async (oid) => {
    try {
      await api.post(`/offers/${oid}/select`);
      const o = data.supplier_offers.find((x) => x.id === oid);
      setQuote((q) => ({ ...q, cost_amount: String(o.amount), cost_currency: o.currency }));
      toast.success("Tedarikçi teklifi seçildi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const createQuote = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quotes", {
        request_id: id, supplier_offer_id: selected?.id || null,
        cost_amount: Number(quote.cost_amount), cost_currency: quote.cost_currency,
        sale_amount: Number(quote.sale_amount), sale_currency: quote.sale_currency,
        valid_until: quote.valid_until || null, fx_locked: quote.fx_locked, notes: quote.notes,
      });
      toast.success("Satış teklifi oluşturuldu");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const approve = async (qid) => {
    try {
      const { data: res } = await api.post(`/quotes/${qid}/approve`);
      toast.success(`Operasyon dosyası açıldı: ${res.operation.no}`);
      nav(`/operasyonlar/${res.operation.id}`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="request-detail-page">
      <button onClick={() => nav("/talepler")} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-950" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Talepler
      </button>

      <div className="brut p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="overline">Talep</p>
            <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="request-no">{req.no}</h1>
            <p className="mt-1 text-sm text-zinc-600" data-testid="request-summary">
              {req.customer_name} · {req.origin} → {req.destination} · {MODE_LABELS[req.mode]}
            </p>
          </div>
          <Badge value={req.status} testId="request-status-badge" />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 border-t border-zinc-200 pt-4 text-sm sm:grid-cols-4">
          <div><p className="overline">Yük</p><p>{req.cargo_desc}</p></div>
          <div><p className="overline">Taşıma Birimi</p><p className="font-mono" data-testid="request-equipment">{req.equipment || "-"}</p></div>
          <div><p className="overline">Ağırlık</p><p className="tnum font-mono">{req.weight_kg} kg</p></div>
          <div><p className="overline">Hacim</p><p className="tnum font-mono">{req.volume_m3} m³</p></div>
          <div><p className="overline">İşlem Tipi</p><p data-testid="request-direction">{DIRECTION_LABELS[req.direction] || "Export"}</p></div>
          <div><p className="overline">Incoterm</p><p>{req.incoterm || "-"}</p></div>
          <div><p className="overline">Hazır</p><p>{d(req.ready_date)}</p></div>
        </div>
        {(req.description || req.notes) && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t border-zinc-200 pt-4 text-sm sm:grid-cols-2">
            {req.description && <div><p className="overline">Açıklama</p><p className="whitespace-pre-line" data-testid="request-description">{req.description}</p></div>}
            {req.notes && <div><p className="overline">Not</p><p className="whitespace-pre-line" data-testid="request-notes">{req.notes}</p></div>}
          </div>
        )}
      </div>

      <div className="brut" data-testid="tariffs-panel">
        <div className="border-b border-zinc-950 px-4 py-3"><p className="overline">Uygun Tarife Seçenekleri</p></div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead><tr><th>Tedarikçi</th><th>Tip</th><th>Rota</th><th>Birim</th><th className="!text-right">Fiyat</th><th className="!text-right">TRY</th></tr></thead>
            <tbody>
              {data.tariffs.map((t, i) => (
                <tr key={t.id} className={i % 2 ? "bg-zinc-50" : ""}>
                  <td>{t.supplier_name}</td><td>{t.kind === "spot" ? "Spot" : "Sözleşmeli"}</td>
                  <td className="text-xs">{t.origin} → {t.destination}</td><td>{t.unit}</td>
                  <td className="tnum text-right font-mono">{money(t.price, t.currency)}</td>
                  <td className="tnum text-right font-mono">{money(t.price_try)}</td>
                </tr>
              ))}
              {data.tariffs.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-zinc-500">Bu mod için tanımlı tarife yok.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="brut" data-testid="offers-panel">
        <div className="border-b border-zinc-950 px-4 py-3"><p className="overline">Tedarikçi Teklifleri</p></div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead><tr><th>Tedarikçi</th><th className="!text-right">Tutar</th><th className="!text-right">TRY</th><th>Transit</th><th>Kur</th><th></th></tr></thead>
            <tbody>
              {data.supplier_offers.map((o, i) => (
                <tr key={o.id} className={o.selected ? "bg-green-50" : i % 2 ? "bg-zinc-50" : ""} data-testid={`offer-row-${o.id}`}>
                  <td className="font-semibold">{o.supplier_name}</td>
                  <td className="tnum text-right font-mono">{money(o.amount, o.currency)}</td>
                  <td className="tnum text-right font-mono">{money(o.amount_try)}</td>
                  <td className="tnum">{o.transit_days} gün</td>
                  <td className="font-mono text-xs">{o.fx_rate}</td>
                  <td className="text-right">
                    {o.selected ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700"><Check className="h-3 w-3" /> Seçili</span>
                    ) : can("quote:write") ? (
                      <button className="brut-btn-ghost !px-2 !py-1 !text-xs" onClick={() => select(o.id)} data-testid={`select-offer-${o.id}`}>Seç</button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {data.supplier_offers.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-zinc-500">Henüz tedarikçi teklifi yok.</td></tr>}
            </tbody>
          </table>
        </div>
        {can("quote:write") && (
          <form onSubmit={addOffer} className="grid grid-cols-1 gap-3 border-t border-zinc-200 p-4 sm:grid-cols-5" data-testid="offer-form">
            <select required className="brut-input" value={offer.supplier_id}
              onChange={(e) => setOffer({ ...offer, supplier_id: e.target.value })} data-testid="offer-supplier-select">
              <option value="">Tedarikçi</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input required type="number" step="0.01" placeholder="Tutar" className="brut-input" value={offer.amount}
              onChange={(e) => setOffer({ ...offer, amount: e.target.value })} data-testid="offer-amount-input" />
            <select className="brut-input" value={offer.currency}
              onChange={(e) => setOffer({ ...offer, currency: e.target.value })} data-testid="offer-currency-select">
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input type="number" placeholder="Transit gün" className="brut-input" value={offer.transit_days}
              onChange={(e) => setOffer({ ...offer, transit_days: e.target.value })} data-testid="offer-transit-input" />
            <button className="brut-btn" disabled={busy} data-testid="offer-save-button">Teklif Ekle</button>
          </form>
        )}
      </div>

      <div className="brut" data-testid="quotes-panel">
        <div className="border-b border-zinc-950 px-4 py-3"><p className="overline">Satış Teklifleri</p></div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead><tr><th>Teklif No</th><th>Rev.</th><th className="!text-right">Maliyet</th><th className="!text-right">Satış</th><th className="!text-right">Kâr</th><th className="!text-right">Marj</th><th>Geçerlilik</th><th>Durum</th><th></th></tr></thead>
            <tbody>
              {data.quotes.map((q, i) => (
                <tr key={q.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`quote-row-${q.id}`}>
                  <td className="font-mono font-semibold">{q.no}</td>
                  <td className="tnum">{q.revision}</td>
                  <td className="tnum text-right font-mono">{money(q.cost_try)}</td>
                  <td className="tnum text-right font-mono">{money(q.sale_try)}</td>
                  <td className="tnum text-right font-mono">{money(q.profit_try)}</td>
                  <td className="text-right"><MarginTag pct={q.margin_pct} /></td>
                  <td className="text-xs">{d(q.valid_until)}</td>
                  <td><Badge value={q.status} /></td>
                  <td className="text-right">
                    {q.status !== "onaylandi" && can("quote:write") && (
                      <button className="brut-btn !px-2 !py-1 !text-xs" onClick={() => approve(q.id)} data-testid={`approve-quote-${q.id}`}>
                        Müşteri Onayı & Dosya Aç
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {data.quotes.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-zinc-500">Henüz satış teklifi yok.</td></tr>}
            </tbody>
          </table>
        </div>
        {can("quote:write") && (
          <form onSubmit={createQuote} className="grid grid-cols-1 gap-3 border-t border-zinc-200 p-4 sm:grid-cols-6" data-testid="quote-form">
            <input required type="number" step="0.01" placeholder="Maliyet" className="brut-input" value={quote.cost_amount}
              onChange={(e) => setQuote({ ...quote, cost_amount: e.target.value })} data-testid="quote-cost-input" />
            <select className="brut-input" value={quote.cost_currency}
              onChange={(e) => setQuote({ ...quote, cost_currency: e.target.value })} data-testid="quote-cost-currency">
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input required type="number" step="0.01" placeholder="Satış" className="brut-input" value={quote.sale_amount}
              onChange={(e) => setQuote({ ...quote, sale_amount: e.target.value })} data-testid="quote-sale-input" />
            <select className="brut-input" value={quote.sale_currency}
              onChange={(e) => setQuote({ ...quote, sale_currency: e.target.value })} data-testid="quote-sale-currency">
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input type="date" className="brut-input" value={quote.valid_until}
              onChange={(e) => setQuote({ ...quote, valid_until: e.target.value })} data-testid="quote-validuntil-input" />
            <button className="brut-btn" disabled={busy} data-testid="quote-save-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Teklif Oluştur
            </button>
            <label className="flex items-center gap-2 text-xs text-zinc-600 sm:col-span-6">
              <input type="checkbox" checked={quote.fx_locked}
                onChange={(e) => setQuote({ ...quote, fx_locked: e.target.checked })} data-testid="quote-fxlock-checkbox" />
              Kuru sabitle (teklif onaylandığında kur değişse de teklif kuru korunur)
            </label>
          </form>
        )}
      </div>
    </div>
  );
}
